import { AlertColor, Box, CircularProgress } from '@mui/material';
import { CustomSearchBox } from 'components/app-container/custom-search-box/CustomSearchBox';
import UPSnackBar from 'components/app-container/snackbar/UPSnackbar';
import UPButton from 'components/button';
import UPEmptyState from 'components/empty-state';
import ErrorBar from 'components/error-bar';
import { UPTanStackTable } from 'components/tanstack-table';
import { useGridPagination } from 'hooks/useGridPagination';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ABSENCE_REASON__ADD,
  ABSENCE_REASON__DEACTIVATE_SUCCESS,
  ABSENCE_REASON__EMPTY_STATE,
  ABSENCE_REASON__SAVED,
  DATAGRID__NO_ROWS,
  GENERAL__RETRY,
} from 'translations/constants';
import { IAbsenceReason } from 'types/absence-reason/IAbsenceReason';
import AbsenceReasonPanel from './AbsenceReasonPanel';
import { buildAbsenceReasonColumns } from './columns';
import { INITIAL_PAGE_SIZE, PAGE_SIZE_OPTIONS } from './constants';
import { useAbsenceReasonPanel } from './hooks/useAbsenceReasonPanel';
import { useAbsenceReasonsOverview } from './hooks/useAbsenceReasonsOverview';
import {
  AbsenceContainer,
  AbsenceSearchWrapper,
  AbsenceSectionBody,
  AbsenceSectionContainer,
  AbsenceToolbar,
} from './styles';
import { filterAbsenceReasons } from './utils';

const getRowId = (row: IAbsenceReason) => row.id;
const getRowTestId = (row: IAbsenceReason) => `absence-reason-row-${row.id}`;

interface Toast {
  id: number;
  type: AlertColor;
  content: string;
}

const AbsenceReasons = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const overview = useAbsenceReasonsOverview();
  const [searchTerm, setSearchTerm] = useState('');
  const filteredRows = useMemo(
    () => filterAbsenceReasons(overview.rows, searchTerm, i18n.language),
    [overview.rows, searchTerm, i18n.language],
  );
  // Owns the page index, so a reload after Save or Close keeps the user on the page they were on.
  const { pagination, setPagination, resetToFirstPage } = useGridPagination(filteredRows, getRowId, INITIAL_PAGE_SIZE);
  const onSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );
  // An incrementing counter, not Date.now(): two toasts pushed in the same millisecond must not share a key.
  const toastId = useRef(0);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const pushToast = useCallback((type: AlertColor, content: string) => {
    toastId.current += 1;
    const id = toastId.current;
    setToasts(previous => [...previous, { id, type, content }]);
  }, []);
  const dismissToast = (id: number) => setToasts(previous => previous.filter(toast => toast.id !== id));

  const { reload } = overview;
  const panel = useAbsenceReasonPanel({
    onSaved: () => {
      reload();
      pushToast('success', t(ABSENCE_REASON__SAVED));
    },
    onDeactivated: () => {
      reload();
      pushToast('success', t(ABSENCE_REASON__DEACTIVATE_SUCCESS));
    },
    onClosed: () => reload(),
    onGone: (messageKey: string) => {
      reload();
      pushToast('error', t(messageKey));
    },
    onStale: () => reload(),
    onError: (messageKey: string) => pushToast('error', t(messageKey)),
  });

  const { openAdd, openEdit } = panel;
  const onEdit = useCallback((row: IAbsenceReason) => openEdit(row), [openEdit]);
  const columns = useMemo(() => buildAbsenceReasonColumns(t, i18n.language, onEdit), [t, i18n.language, onEdit]);

  const renderBody = () => {
    const loadError = overview.loadErrorKey && (
      <Box
        data-testid="absence-reasons-load-error"
        sx={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}
      >
        <ErrorBar message={t(overview.loadErrorKey)} />
        <UPButton text={t(GENERAL__RETRY)} onClick={overview.reload} outlined testId="absence-reasons-load-retry" />
      </Box>
    );
    // A failed first load replaces the page body; a failed later reload keeps the rows already shown.
    if (!overview.hasLoaded && loadError) return loadError;
    if (!overview.hasLoaded) return <CircularProgress data-testid="absence-reasons-loading" />;
    if (overview.rows.length === 0) {
      return (
        <>
          {loadError}
          <UPEmptyState messageKey={ABSENCE_REASON__EMPTY_STATE} testId="absence-reasons-empty-state" />
        </>
      );
    }
    return (
      <>
        {loadError}
        <UPTanStackTable
          data={filteredRows}
          columns={columns}
          getRowId={getRowId}
          getRowTestId={getRowTestId}
          testId="absence-reasons-table"
          emptyMessage={t(DATAGRID__NO_ROWS)}
          enableSorting={false}
          pagination={pagination}
          onPaginationChange={setPagination}
          autoResetPageIndex={false}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          sx={{
            height: 'auto',
            '& .MuiTableContainer-root': { maxHeight: 'none', overflowY: 'visible' },
            '& thead th:last-of-type:hover': { backgroundColor: 'common.white' },
          }}
        />
      </>
    );
  };

  return (
    <AbsenceSectionContainer>
      <AbsenceSectionBody>
        <AbsenceContainer>
          <AbsenceToolbar>
            <AbsenceSearchWrapper>
              <CustomSearchBox onSearch={onSearch} testId="absence-reasons-search" />
            </AbsenceSearchWrapper>
            <UPButton text={`+ ${t(ABSENCE_REASON__ADD)}`} onClick={openAdd} testId="absence-reasons-add" />
          </AbsenceToolbar>
          {renderBody()}
          <AbsenceReasonPanel panel={panel} />
          {toasts.map((toast, index) => (
            <UPSnackBar
              key={toast.id}
              open
              type={toast.type}
              content={toast.content}
              onClose={() => dismissToast(toast.id)}
              vertical="bottom"
              horizontal="right"
              index={index}
            />
          ))}
        </AbsenceContainer>
      </AbsenceSectionBody>
    </AbsenceSectionContainer>
  );
};

export default AbsenceReasons;
